/**
 * KOVA Empty State.
 *
 * Shared "no data yet" component so every module inherits the same friendly
 * empty view. Slots: `icon`, `title`, `description`, `action`.
 *
 * Variants:
 *   - "block"   → full block, use in main content areas (default).
 *   - "inline"  → compact horizontal, use inside cards or tables.
 *   - "row"     → single table row placeholder (colSpan required).
 */
import React from "react";
import { Inbox } from "lucide-react";

export default function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  variant = "block",
  className = "",
  testid = "empty-state",
}) {
  if (variant === "row") {
    return (
      <tr data-testid={testid}>
        <td colSpan={action?.colSpan || 99} className="px-6 py-16 text-center">
          <EmptyBody icon={Icon} title={title} description={description} action={action} compact />
        </td>
      </tr>
    );
  }
  if (variant === "inline") {
    return (
      <div
        className={`flex items-center gap-4 p-4 rounded-[12px] border border-dashed border-[#3A4640] bg-[#0B0F0C]/40 ${className}`}
        data-testid={testid}
      >
        <span className="w-10 h-10 rounded-[10px] bg-[#121815] border border-[#3A4640] flex items-center justify-center shrink-0">
          <Icon size={18} className="text-[#39FF14]" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-heading font-semibold text-[#F8F9FA] truncate">{title}</div>
          {description && <div className="text-xs text-[#8A8F98] truncate">{description}</div>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    );
  }
  return (
    <div
      className={`tzat-card px-6 py-12 flex flex-col items-center text-center ${className}`}
      data-testid={testid}
    >
      <EmptyBody icon={Icon} title={title} description={description} action={action} />
    </div>
  );
}

function EmptyBody({ icon: Icon, title, description, action, compact }) {
  return (
    <>
      <div
        className={`${compact ? "w-12 h-12" : "w-16 h-16"} mx-auto rounded-[14px] bg-[#121815] border border-[#3A4640] flex items-center justify-center relative`}
      >
        <span className="absolute inset-0 rounded-[14px] bg-[#39FF14]/5 blur-md" aria-hidden />
        <Icon size={compact ? 20 : 26} className="text-[#39FF14] relative" />
      </div>
      {title && (
        <div className={`${compact ? "mt-3" : "mt-5"} font-heading font-semibold text-[#F8F9FA] ${compact ? "text-sm" : "text-lg"}`}>
          {title}
        </div>
      )}
      {description && (
        <p className={`text-[#8A8F98] max-w-md mx-auto ${compact ? "text-xs mt-1" : "text-sm mt-2"}`}>{description}</p>
      )}
      {action && <div className={`${compact ? "mt-3" : "mt-5"}`}>{action}</div>}
    </>
  );
}
